import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { Field, useFormikContext } from "formik";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useSettings } from "../../hooks";
import { Fieldset, WideBadge, LabelsContainer, BadgeContainer } from "./ConfigureValuebetModal.styled";
import { HelpButton } from "../HelpButton/HelpButton";
import _pull from "lodash/pull";

export const ConfigureValuebetFormSubscription = observer(({ subscription }) => {
  const [selectedSport, setSelectedSport] = useState("football");
  const [leagueInput, setLeagueInput] = useState("");
  const { isLive, options } = useSettings();

  const { handleChange, setFieldValue, values } = useFormikContext();
  // the values of this specific subscription
  const subscriptionValues = subscription === "default" ? values : values.subscriptionConfigs[subscription];
  const namePrefix = subscription === "default" ? "" : `subscriptionConfigs.${subscription}.`;

  // Do not show fields when using the default configuration
  if (subscriptionValues === undefined) return null;

  const excludedLeagues = subscriptionValues.excludedLeagues || [];

  // Return the correct field name
  const fieldName = (name) => {
    return `${namePrefix}${name}`;
  };

  // Add a text to the list of leagues to be excluded
  const addLeagueExclusion = () => {
    if (leagueInput === undefined || leagueInput === "") return;
    if (!excludedLeagues.includes(leagueInput)) {
      excludedLeagues.push(leagueInput);
      setFieldValue(fieldName("excludedLeagues"), excludedLeagues);
    }
    setLeagueInput("");
  };

  const onLeagueInputKeyDown = (event) => {
    if (event.key == "Enter") {
      event.preventDefault();
      addLeagueExclusion();
    }
  };

  // Called when a sport is checked/unchecked
  const onSportsChange = (event) => {
    handleChange(event); // call formik's handleChange
    // Select sport on markets dropdown when it is checked so that we easily see the markets
    if (event.target.checked) setSelectedSport(event.target.value);
    else {
      // Change selected sport on markets dropdown if it has been unchecked
      if (event.target.value === selectedSport) {
        const firstSport = options.sports.find(
          ({ sport }) => sport !== selectedSport && subscriptionValues.sports.includes(sport)
        );
        setSelectedSport((firstSport || options.sports[0])?.sport);
      }
    }
  };

  const removeLeagueExclusion = (league) => {
    _pull(excludedLeagues, league);
    setFieldValue(fieldName("excludedLeagues"), excludedLeagues);
  };

  return (
    <Fieldset>
      <LabelsContainer>
        <Form.Label>Deportes</Form.Label>
        <BadgeContainer role="group" aria-labelledby="checkbox-group">
          {options.sports.map(({ sport, label }) => (
            <WideBadge key={sport} variant="secondary" forwardedAs="label" checked={(values?.sports||[]).includes(sport)}>
              <Field type="checkbox" name={fieldName("sports")} value={sport} onChange={onSportsChange} /> {label}
            </WideBadge>
          ))}
        </BadgeContainer>
      </LabelsContainer>
      <LabelsContainer style={{ display: (subscriptionValues?.sports?.length ?? 0) > 0 ? null : "none" }}>
        <Form.Label>Mercados</Form.Label>
        <Form.Control
          onChange={(event) => setSelectedSport(event.target.value)}
          as="select"
          value={selectedSport}
          style={{ marginBottom: "10px" }}
        >
          {options.sports.map(({ sport, label }) => (
            <option key={sport} value={sport} disabled={!subscriptionValues.sports.includes(sport)}>
              {label}
            </option>
          ))}
        </Form.Control>
        <BadgeContainer role="group" aria-labelledby="checkbox-group">
          {Object.entries(options.markets)
            .map(([sport, markets]) =>
              markets.map((market) => (
                <OverlayTrigger
                  key={`${sport}-${market}`}
                  placement="top"
                  delay={{ show: 50, hide: 200 }}
                  overlay={<Tooltip id="button-tooltip-2">{options.marketsTranslation[market] || market}</Tooltip>}
                >
                  <WideBadge
                    variant="secondary"
                    forwardedAs="label"
                    hide={sport !== selectedSport}
                    data-toggle="tooltip"
                    data-placement="top"
                    title={options.marketsTranslation[market] || market}
                    checked={(values?.sportsMarkets?.[sport]||[]).includes(market)}
                  >
                    <Field type="checkbox" name={fieldName(`sportsMarkets.${sport}`)} value={market} /> {market}
                  </WideBadge>
                </OverlayTrigger>
              ))
            )
            .flat()}
        </BadgeContainer>
      </LabelsContainer>
      <LabelsContainer>
      <Form.Label>Porcentaje</Form.Label>
      <div style={{ display: "flex" }}>
        <Form.Group>
          <Form.Control
            as={Field}
            placeholder="Min"
            type="number"
            name={fieldName("minPercentage")}
            min="0"
            max="300"
          />
        </Form.Group>
        <Form.Group>
          <Form.Control
            as={Field}
            placeholder="Max"
            type="number"
            name={fieldName("maxPercentage")}
            min="0"
            max="300"
          />
        </Form.Group>
      </div>
      </LabelsContainer>
      {isLive && (
        <>
         <LabelsContainer>
          <Form.Label>Tiempo de Valuebet (Segundos)</Form.Label>
          <div style={{ display: "flex" }}>
            <Form.Group>
              <Form.Control as={Field} placeholder="Min" type="number" name={fieldName("minTime")} min="0" />
            </Form.Group>
            <Form.Group>
              <Form.Control as={Field} placeholder="Max" type="number" name={fieldName("maxTime")} />
            </Form.Group>
          </div>
          </LabelsContainer>
        </>
      )}
      {isLive && <Form.Group controlId={fieldName("showTimeSinceLastUpdate")}>
        <LabelsContainer>
        <Form.Label>Mostrar tiempo desde la última actualización</Form.Label>
        <HelpButton id="leagues-help" placement="right-start" width="340px">
          Activalo si quieres ver cuánto tiempo hace desde el último time off o el último punto anotado.
        </HelpButton>
        <Form.Control
          name={fieldName("showTimeSinceLastUpdate")}
          onChange={handleChange}
          as="select"
          value={subscriptionValues.showTimeSinceLastUpdate}
          label="Mostrar tiempo desde la última actualización"
        >
          <option value={true}>Activar</option>
          <option value={false}>Desactivar</option>
        </Form.Control>
        </LabelsContainer>
      </Form.Group> }
      {isLive && <Form.Group controlId={fieldName("showNextPeriodExtraBet")}>
        <LabelsContainer>
        <Form.Label>Mostrar apuestas del próximo periodo con prioridad</Form.Label>
        <HelpButton id="leagues-help" placement="right-start" width="340px">
          Activalo si quieres que aparte de la primera apuesta te aparezca otra del próximo periodo.
        </HelpButton>
        <Form.Control
          name={fieldName("showNextPeriodExtraBet")}
          onChange={handleChange}
          as="select"
          value={subscriptionValues.showNextPeriodExtraBet}
          label="Mostrar apuestas del próximo periodo con prioridad"
        >
          <option value={true}>Activar</option>
          <option value={false}>Desactivar</option>
        </Form.Control>
        </LabelsContainer>
      </Form.Group> }
      {isLive && (
        <>
        <LabelsContainer>
          <Form.Label>Mostrar solo siguiente periodo</Form.Label>
          <BadgeContainer role="group" aria-labelledby="checkbox-group">
            {options.nextPeriodFilteringSports.map(({ sport, label }) => (
              <WideBadge 
              key={sport} 
              variant="secondary" 
              forwardedAs="label" 
              checked={(values?.nextPeriodSports||[]).includes(sport)}
              >
                <Field type="checkbox" name={fieldName("nextPeriodSports")} value={sport} /> {label}
              </WideBadge>
            ))}
          </BadgeContainer>
          </LabelsContainer>
          <LabelsContainer>
          <Form.Label>Mostrar solo en pausas</Form.Label>
          <BadgeContainer role="group" aria-labelledby="checkbox-group">
            {options.pausedFilterSports.map(({ sport, label }) => (
              <WideBadge 
                key={sport} 
                variant="secondary" 
                forwardedAs="label"
                checked={(values?.pausedSports||[]).includes(sport)}
                >
                <Field type="checkbox" name={fieldName("pausedSports")} value={sport} /> {label}
              </WideBadge>
            ))}
          </BadgeContainer>
          </LabelsContainer>
        </>
      )}
      <Form.Group>
        <LabelsContainer>
        <Form.Label>Excluir competiciones</Form.Label>
        <HelpButton id="leagues-help" placement="right-start" width="340px">
          Añade texto que coincida (al menos en parte) con el nombre de la/s competicion/es que quieres que no se
          muestren. Por ejemplo, si añades &quot;ITF&quot; no se va a mostrar ninguna competición que contenga
          &quot;ITF&quot; en el nombre, tanto &quot;ITF M15 Cancun&quot; como &quot;ITF M25 Lesa&quot; o cualquier otra.
        </HelpButton>
        <BadgeContainer role="group" aria-labelledby="checkbox-group">
          {excludedLeagues.map((league) => (
            <WideBadge key={league} variant="secondary" clickable={false}>
              {league}
              <Button
                variant="outline-secondary"
                className="close"
                aria-label="Remove"
                style={{ color: "#1d2d1d" }}
                onClick={() => removeLeagueExclusion(league)}
              >
                <span aria-hidden="true">&times;</span>
              </Button>
            </WideBadge>
          ))}
        </BadgeContainer>
        <InputGroup>
          <Form.Control
            as={Field}
            placeholder="Nombre de la competición"
            type="text"
            value={leagueInput}
            onChange={(event) => setLeagueInput(event.target.value)}
            onKeyDown={onLeagueInputKeyDown}
          />
          <InputGroup.Append>
            <Button variant="primary" onClick={addLeagueExclusion} style={{ background: '#1BB78A' }}>
              +
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </LabelsContainer>
      </Form.Group>
      <Form.Group>
        <LabelsContainer>
          <Form.Label>Cuotas</Form.Label>
          <HelpButton id="modo-middle-help">
            Muestra solo sures con las cuotas en un rango determinado.
          </HelpButton>
          <div style={{ display: "flex" }}>
            <Form.Group>
              <Form.Control
                as={Field}
                placeholder="Min"
                type="decimal"
                name={fieldName("minOdds")}
                min="0"
                max="100"
              />
            </Form.Group>
            <Form.Group>
              <Form.Control
                as={Field}
                placeholder="Max"
                type="decimal"
                name={fieldName("maxOdds")}
                min="0"
                max="100000"
              />
            </Form.Group>
          </div>
        </LabelsContainer>
      </Form.Group>
    </Fieldset>
  );
});
